import * as THREE from 'three';
import { VolumetricCloud } from './VolumetricCloud';
import { FeeZoneManager } from './FeeZoneManager';
import { NebulaParticles } from '../particles/NebulaParticles';
import { AmbientDust } from '../particles/AmbientDust';
import { Transaction } from '../../core/models/Transaction';
import { MempoolInfo } from '../../core/models/NetworkStats';

export interface MempoolNebulaConfig {
  center: THREE.Vector3;
  innerRadius: number;
  outerRadius: number;
  height: number;
  maxParticles: number;
  qualityLevel: 'low' | 'medium' | 'high' | 'ultra';
}

export interface MempoolStats {
  size: number;        // MB
  count: number;       // transaction count
  feeHistogram: Array<{ feeRate: number; count: number }>;
  avgFeeRate: number;
}

export class MempoolNebula extends THREE.Group {
  private config: MempoolNebulaConfig;
  
  // Visual components
  public volumetricCloud: VolumetricCloud;
  public particleSystem: NebulaParticles;
  public feeZones: FeeZoneManager;
  public ambientDust: AmbientDust;
  
  // Nebula properties
  public bounds: {
    center: THREE.Vector3;
    innerRadius: number;
    outerRadius: number;
    height: number;
  };
  
  // Dynamic properties
  public density: number = 0.1;
  public turbulence: number = 0.1;
  public avgFeeRate: number = 1;
  public congestionLevel: 'low' | 'medium' | 'high' | 'critical' = 'low';
  
  // Performance tracking
  private lastUpdate: number = 0;
  private frameCount: number = 0;
  
  constructor(config: Partial<MempoolNebulaConfig> = {}) {
    super();
    
    this.config = {
      center: new THREE.Vector3(0, 0, 0),
      innerRadius: 15,
      outerRadius: 35,
      height: 20,
      maxParticles: 1000,
      qualityLevel: 'high',
      ...config,
    };
    
    this.bounds = {
      center: this.config.center.clone(),
      innerRadius: this.config.innerRadius,
      outerRadius: this.config.outerRadius,
      height: this.config.height,
    };
    
    this.position.copy(this.config.center);
    this.userData = { id: 'mempool-nebula', type: 'nebula' };
    
    this.createNebulaComponents();
    this.setupInitialState();
  }
  
  private createNebulaComponents(): void {
    // Create volumetric cloud system
    this.volumetricCloud = new VolumetricCloud({
      radius: this.config.outerRadius,
      height: this.config.height,
      resolution: this.getCloudResolution(),
      qualityLevel: this.config.qualityLevel,
    });
    this.add(this.volumetricCloud);
    
    // Create fee zone manager
    this.feeZones = new FeeZoneManager({
      innerRadius: this.config.innerRadius,
      outerRadius: this.config.outerRadius,
      height: this.config.height,
    });
    this.add(this.feeZones);
    
    // Create particle system for transactions
    this.particleSystem = new NebulaParticles({
      maxParticles: this.config.maxParticles,
      bounds: this.bounds,
      qualityLevel: this.config.qualityLevel,
    });
    this.add(this.particleSystem);
    
    // Create ambient dust for atmosphere
    this.ambientDust = new AmbientDust({
      bounds: this.bounds,
      particleCount: this.getAmbientParticleCount(),
      qualityLevel: this.config.qualityLevel,
    });
    this.add(this.ambientDust);
  }
  
  private getCloudResolution(): THREE.Vector3 {
    const qualityMap = {
      low: new THREE.Vector3(32, 16, 32),
      medium: new THREE.Vector3(64, 32, 64),
      high: new THREE.Vector3(128, 64, 128),
      ultra: new THREE.Vector3(256, 128, 256),
    };
    return qualityMap[this.config.qualityLevel];
  }
  
  private getAmbientParticleCount(): number {
    const qualityMap = {
      low: 200,
      medium: 500,
      high: 1000,
      ultra: 2000,
    };
    return qualityMap[this.config.qualityLevel];
  }
  
  private setupInitialState(): void {
    // Set up initial nebula appearance for empty mempool
    this.updateMempoolStats({
      size: 1,
      count: 100,
      feeHistogram: [
        { feeRate: 1, count: 50 },
        { feeRate: 10, count: 30 },
        { feeRate: 50, count: 15 },
        { feeRate: 100, count: 5 },
      ],
      avgFeeRate: 15,
    });
  }
  
  public updateMempoolStats(stats: MempoolStats): void {
    this.avgFeeRate = stats.avgFeeRate;
    
    // Calculate density based on mempool size (0-1 scale)
    // 100MB+ mempool = full density
    this.density = Math.min(stats.size / 100, 1.0);
    
    // Calculate turbulence based on transaction count
    // 100k+ transactions = max turbulence
    this.turbulence = Math.min(stats.count / 100000, 1.0);
    
    // Calculate congestion level
    this.congestionLevel = this.calculateCongestionLevel(stats.size, stats.count);
    
    // Update visual components
    this.volumetricCloud.updateDensityField(this.density, this.turbulence);
    this.feeZones.updateFeeDistribution(stats.feeHistogram);
    this.particleSystem.updateMempoolStats(stats);
    this.ambientDust.updateCongestion(this.congestionLevel);
  }

  public updateFromMempoolStats(stats: MempoolStats): void {
    this.updateMempoolStats(stats);
  }

  public setTurbulence(turbulenceLevel: number): void {
    this.turbulence = Math.max(0, Math.min(1, turbulenceLevel));
    
    // Update cloud turbulence if method exists
    if (this.volumetricCloud && 'setTurbulence' in this.volumetricCloud) {
      (this.volumetricCloud as any).setTurbulence(this.turbulence);
    }
    
    // Update particle system turbulence if method exists
    if (this.particleSystem && 'setTurbulence' in this.particleSystem) {
      (this.particleSystem as any).setTurbulence(this.turbulence);
    }
  }

  public setDensity(densityLevel: number): void {
    this.density = Math.max(0, Math.min(1, densityLevel));
    
    // Update cloud density if method exists
    if (this.volumetricCloud && 'setDensity' in this.volumetricCloud) {
      (this.volumetricCloud as any).setDensity(this.density);
    }
    
    // Update ambient dust density if method exists
    if (this.ambientDust && 'setDensity' in this.ambientDust) {
      (this.ambientDust as any).setDensity(this.density);
    }
  }
  
  private calculateCongestionLevel(size: number, count: number): 'low' | 'medium' | 'high' | 'critical' {
    if (size > 300 || count > 150000) return 'critical';
    if (size > 150 || count > 100000) return 'high';
    if (size > 50 || count > 50000) return 'medium';
    return 'low';
  }
  
  public spawnTransaction(tx: Transaction): THREE.Vector3 {
    // Get spawn zone based on fee rate
    const spawnZone = this.getSpawnZone(tx.feeRate || 1);
    
    // Add transaction particle to system
    this.particleSystem.spawn(tx);
    
    return spawnZone;
  }
  
  public getSpawnZone(feeRate: number): THREE.Vector3 {
    // Get position from fee zone manager
    const zonePosition = this.feeZones.getZonePosition(feeRate);
    
    // Add some random variance
    const variance = new THREE.Vector3(
      (Math.random() - 0.5) * 4,
      (Math.random() - 0.5) * 2,
      (Math.random() - 0.5) * 4
    );
    
    // Convert to world space
    const worldPosition = new THREE.Vector3(
      Math.cos(zonePosition.radius) * zonePosition.radius,
      zonePosition.height,
      Math.sin(zonePosition.radius) * zonePosition.radius
    ).add(variance);
    
    return worldPosition;
  }

  /**
   * Phase 2: Check if a position is within the nebula boundary
   */
  public isWithinBounds(position: THREE.Vector3): boolean {
    const distance = position.distanceTo(this.bounds.center);
    const heightDiff = Math.abs(position.y - this.bounds.center.y);
    
    return distance <= this.bounds.outerRadius && heightDiff <= this.bounds.height / 2;
  }

  /**
   * Phase 2: Check if a ship is entering the nebula (approaching boundary)
   * Returns distance to boundary (positive = outside, negative = inside)
   */
  public getDistanceToBoundary(position: THREE.Vector3): number {
    const distance = position.distanceTo(this.bounds.center);
    return distance - this.bounds.outerRadius;
  }

  /**
   * Phase 2: Calculate fee-based layer position within nebula
   * Higher fees get positioned closer to center (inner layers)
   */
  public getFeeLayerPosition(feeRate: number, shipId: string): THREE.Vector3 {
    // Normalize fee rate to determine layer (0 = outer, 1 = inner)
    const feeNormalized = Math.min(feeRate / 200, 1.0); // 200 sat/vB = max priority
    
    // Calculate target radius (higher fees = smaller radius = closer to center)
    // Leave some margin from outerRadius to ensure ships stay within bounds
    const safeOuterRadius = this.bounds.outerRadius * 0.9; // 10% margin
    const targetRadius = this.bounds.innerRadius + 
      (safeOuterRadius - this.bounds.innerRadius) * (1 - feeNormalized);
    
    // Use ship ID for deterministic but varied positioning
    const hash = this.hashString(shipId);
    const angle = (hash % 1000) / 1000 * Math.PI * 2;
    
    // Smaller radius variation that won't push ships outside bounds
    const radiusVariation = (hash % 100) / 100 * 0.1 - 0.05; // ±5% variation
    const finalRadius = Math.max(
      this.bounds.innerRadius, 
      Math.min(safeOuterRadius, targetRadius * (1 + radiusVariation))
    );
    
    // Calculate position
    const x = Math.cos(angle) * finalRadius;
    const z = Math.sin(angle) * finalRadius;
    const y = this.bounds.center.y + ((hash % 50) / 50 - 0.5) * this.bounds.height * 0.7; // Reduced height variation
    
    return new THREE.Vector3(x, y, z);
  }

  /**
   * Phase 2: Get orbital parameters for a ship at given fee layer
   */
  public getOrbitalParameters(position: THREE.Vector3, feeRate: number): {
    radius: number;
    angularSpeed: number;
    inclination: number;
  } {
    const radius = position.distanceTo(this.bounds.center);
    
    // Higher fee rate ships orbit faster (more urgent)
    const feeMultiplier = Math.min(feeRate / 100, 2.0);
    const baseSpeed = 0.0005; // radians per frame
    const angularSpeed = baseSpeed * (0.5 + feeMultiplier);
    
    // Random inclination for variety
    const hash = this.hashString(position.x.toString() + position.z.toString());
    const inclination = ((hash % 30) - 15) * Math.PI / 180; // ±15 degrees
    
    return { radius, angularSpeed, inclination };
  }

  /**
   * Simple hash function for deterministic positioning
   */
  private hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    // Ensure we get a meaningful range of values for positioning
    return Math.abs(hash) % 1000000;
  }
  
  public update(deltaTime: number): void {
    this.frameCount++;
    const now = performance.now();
    
    // Update all components
    this.volumetricCloud.update(deltaTime);
    this.feeZones.update(deltaTime);
    this.particleSystem.update(deltaTime);
    this.ambientDust.update(deltaTime);
    
    // Apply LOD optimizations
    this.updateLOD();
    
    this.lastUpdate = now;
  }
  
  private updateLOD(): void {
    // Simple distance-based LOD
    const camera = this.getCamera();
    if (!camera) return;
    
    const distance = camera.position.distanceTo(this.position);
    
    if (distance > 200) {
      // Far distance - reduce quality
      this.volumetricCloud.setLODLevel('low');
      this.particleSystem.setMaxParticles(Math.floor(this.config.maxParticles * 0.3));
    } else if (distance > 100) {
      // Medium distance
      this.volumetricCloud.setLODLevel('medium');
      this.particleSystem.setMaxParticles(Math.floor(this.config.maxParticles * 0.6));
    } else {
      // Close distance - full quality
      this.volumetricCloud.setLODLevel('high');
      this.particleSystem.setMaxParticles(this.config.maxParticles);
    }
  }
  
  private getCamera(): THREE.Camera | null {
    // Find camera in scene
    let current: THREE.Object3D | null = this;
    while (current && !(current instanceof THREE.Scene)) {
      current = current.parent;
    }
    
    if (current instanceof THREE.Scene) {
      const cameras = current.children.filter(child => child instanceof THREE.Camera);
      return cameras.length > 0 ? cameras[0] as THREE.Camera : null;
    }
    
    return null;
  }
  
  public setQualityLevel(level: 'low' | 'medium' | 'high' | 'ultra'): void {
    this.config.qualityLevel = level;
    
    // Update all components
    this.volumetricCloud.setQualityLevel(level);
    this.particleSystem.setQualityLevel(level);
    this.ambientDust.setQualityLevel(level);
    
    // Update resolution and particle counts
    this.volumetricCloud.setResolution(this.getCloudResolution());
    this.ambientDust.setParticleCount(this.getAmbientParticleCount());
  }
  
  public getStatus(): {
    density: number;
    turbulence: number;
    congestionLevel: string;
    activeParticles: number;
    avgFeeRate: number;
    performanceImpact: 'low' | 'medium' | 'high';
  } {
    const activeParticles = this.particleSystem.getActiveParticleCount();
    
    let performanceImpact: 'low' | 'medium' | 'high' = 'low';
    if (activeParticles > 500) performanceImpact = 'high';
    else if (activeParticles > 200) performanceImpact = 'medium';
    
    return {
      density: this.density,
      turbulence: this.turbulence,
      congestionLevel: this.congestionLevel,
      activeParticles,
      avgFeeRate: this.avgFeeRate,
      performanceImpact,
    };
  }
  
  public dispose(): void {
    // Dispose all components
    this.volumetricCloud.dispose();
    this.feeZones.dispose();
    this.particleSystem.dispose();
    this.ambientDust.dispose();
    
    // Clear geometry and materials
    this.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(material => material.dispose());
          } else {
            child.material.dispose();
          }
        }
      }
    });
  }
}